<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { tierBgClass, tierBgStyle, suggestionStatusForPrice } from './pricingTiers';

const props = defineProps({
  price: { type: Object, required: true },
  suggestion: { type: Object, default: null },
});

const { t } = useI18n();

const PILL_CLASS =
  'inline-flex h-6 min-h-6 items-center justify-center rounded px-1.5';

const status = computed(() => {
  if (!props.suggestion || !props.price) return null;
  return suggestionStatusForPrice({
    price: props.price.price,
    unit: props.price.unit,
    currency: props.price.currency,
    tier: props.price.tier,
    suggestedPrice: props.suggestion.price,
    suggestedCurrency: props.suggestion.currency,
    bands: props.suggestion.bands,
    dayHours: props.suggestion.dayHours,
  });
});

const chipClass = computed(() => {
  if (status.value === 'ok') return 'bg-n-brand/15 text-n-brand';
  if (status.value === 'low') return 'bg-n-ruby-3 text-n-ruby-11';
  if (status.value === 'high') return 'bg-sky-600/20 text-sky-700 dark:text-sky-300';
  return '';
});

const chipIcon = computed(() => {
  if (status.value === 'ok') return 'i-lucide-banknote';
  if (status.value === 'low') return 'i-lucide-arrow-up';
  if (status.value === 'high') return 'i-lucide-arrow-down';
  return '';
});

const chipTooltip = computed(() => {
  if (status.value === 'ok') return t('PRICING.MASTER_SHEET.SUGGESTION_OK');
  if (status.value === 'low') return t('PRICING.MASTER_SHEET.SUGGESTION_LOW');
  if (status.value === 'high') return t('PRICING.MASTER_SHEET.SUGGESTION_HIGH');
  return '';
});

const pricePillClass = computed(() => {
  const tierClass = tierBgClass(props.price.tier);
  // Keep a neutral pill background when tier is none so height still matches.
  return tierClass || 'bg-n-alpha-1';
});

const pricePillStyle = computed(() => tierBgStyle(props.price.tier));
</script>

<template>
  <div class="inline-flex items-center justify-end gap-1.5">
    <span
      v-if="status"
      v-tooltip.top="chipTooltip"
      :class="[PILL_CLASS, chipClass]"
    >
      <Icon :icon="chipIcon" class="size-3.5 shrink-0" />
    </span>
    <span
      :class="[PILL_CLASS, pricePillClass, 'font-medium']"
      :style="pricePillStyle"
    >
      {{ price.price }}
    </span>
  </div>
</template>
